/**
 * Shared FAQ types and utilities
 */

export interface FAQItem {
  id: string // Used for both React keys and server communication
  question: string
  answer: string
  // State tracking
  isNew?: boolean // Not yet saved to server
  isPending?: boolean // AI-generated, awaiting approval (bulk generation)
  isDeleted?: boolean // Marked for deletion (will be deleted on save)
  // Original values for tracking modifications to existing FAQs
  originalQuestion?: string
  originalAnswer?: string
  // Per-FAQ AI generation state
  pendingSuggestion?: string // JSON string of suggested FAQ (per-FAQ generation)
  isGenerating?: boolean // Currently generating AI suggestion for this specific FAQ
}

export type FAQState = 'existing' | 'new' | 'pending'

/**
 * Helper function to determine the current state of an FAQ
 */
export function getFAQState(faq: FAQItem): FAQState {
  if (faq.isPending) return 'pending'
  if (faq.isNew) return 'new'
  return 'existing'
}

/**
 * Helper function to check if an FAQ is editable
 * Only approved (non-pending) FAQs are editable
 */
export function isFAQEditable(faq: FAQItem): boolean {
  return !faq.isPending
}

/**
 * Helper function to check if an FAQ has unsaved changes
 */
export function hasFAQUnsavedChanges(faq: FAQItem): boolean {
  return !!(faq.isNew && !faq.isPending && (faq.question.trim() || faq.answer.trim()))
}

/**
 * Helper function to check if an existing FAQ has been modified
 */
export function isFAQModified(faq: FAQItem): boolean {
  if (faq.isNew) return false // New FAQs are tracked separately
  return (
    faq.question.trim() !== (faq.originalQuestion?.trim() || '') ||
    faq.answer.trim() !== (faq.originalAnswer?.trim() || '')
  )
}

/**
 * Validation functions for FAQ content
 */

/**
 * Check if FAQ has valid content (non-empty question and answer)
 */
export function isValidFAQ(faq: FAQItem): boolean {
  return faq.question.trim().length > 0 && faq.answer.trim().length > 0
}

/**
 * Check if FAQ is empty (both question and answer are empty or whitespace)
 */
export function isEmptyFAQ(faq: FAQItem): boolean {
  return faq.question.trim().length === 0 && faq.answer.trim().length === 0
}

/**
 * Check if FAQ is incomplete (has content in one field but not both)
 */
export function isIncompleteFAQ(faq: FAQItem): boolean {
  const hasQuestion = faq.question.trim().length > 0
  const hasAnswer = faq.answer.trim().length > 0
  return hasQuestion !== hasAnswer // XOR - one has content, the other doesn't
}

/**
 * Get validation error message for an FAQ
 */
export function getFAQValidationError(faq: FAQItem, showEmptyErrors: boolean = false): string | null {
  // Show errors for completely empty FAQs only when explicitly requested (e.g., during save validation)
  if (isEmptyFAQ(faq)) {
    return showEmptyErrors ? 'Both question and answer are required' : null
  }

  if (faq.question.trim().length === 0) {
    return 'Question is required'
  }

  if (faq.answer.trim().length === 0) {
    return 'Answer is required'
  }

  return null
}

/**
 * Filter FAQs that are ready to be saved (valid and not pending)
 */
export function getValidFAQsForSaving(faqs: FAQItem[]): FAQItem[] {
  return faqs.filter(faq => faq.isNew && !faq.isPending && isValidFAQ(faq))
}
